import React, { useEffect, useState, FC } from 'react';
import { MapContainer, TileLayer, ZoomControl, LayersControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-defaulticon-compatibility/dist/leaflet-defaulticon-compatibility.css';
import 'leaflet-defaulticon-compatibility';
import 'leaflet-gesture-handling/dist/leaflet-gesture-handling.css';
import L, { LatLngBoundsExpression, Map as MapType } from 'leaflet';
import { GestureHandling } from 'leaflet-gesture-handling';
import '@runette/leaflet-fullscreen';
import VectorTileLayer from 'react-leaflet-vector-tile-layer';

const baseLayers = [
    /*{
        id: 'outdoor',
        name: 'Outdoors',
        checked: false,
        source: 'maptiler',
    },*/
    /*{
        id: 'topo',
        name: 'Topo',
        checked: false,
        source: 'maptiler',
    },*/
    /*{
        id: 'streets',
        name: 'Street',
        checked: false,
        source: 'maptiler',
    },*/
    /*{
        id: 'positron',
        name: 'Light',
        checked: false,
        source: 'maptiler',
    },*/
    {
        id: 'osmpositron',
        name: 'Light',
        checked: false,
        source: 'osm',
    },
    {
        id: 'osm',
        name: 'Open Street Map',
        checked: true,
        source: 'osm',
    },
    {
        id: 'osmcyclo',
        name: 'Open Cycle Map',
        checked: false,
        source: 'osm',
    },
    {
        id: 'osmtopo',
        name: 'Open Topo Map',
        checked: false,
        source: 'osm',
    },
    {
        id: 'osmhotosm',
        name: 'Humanitarian OSM',
        checked: false,
        source: 'osm',
    },
    /*{
        id: 'light_all',
        name: 'Light',
        checked: false,
        source: 'cartocdn',
    },*/
    {
        id: 'satellite',
        name: 'Satellite',
        checked: false,
        source: 'maptiler',
    },
];

// -----------------------------------------------------------------------------------
//
// Map

export interface MapProps {
    name?: string;
    title?: string;
    mapCoords?: {
        lat: number;
        lng: number;
        zoom: number;
    };
    bounds?: LatLngBoundsExpression;
    className?: string;
    maxZoom?: number;
}

const Map: FC<MapProps> = (props): React.ReactElement => {
    const { mapCoords, bounds, children, className, maxZoom } = props;

    const [mapInstance, setMapInstance] = useState<MapType | null>(null);

    useEffect(() => {
        mapInstance?.invalidateSize();
    });

    L.Map.addInitHook('addHandler', 'gestureHandling', GestureHandling);

    const handleWhenCreated = (instance: L.Map) => {
        setMapInstance(instance);

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        instance.gestureHandling.enable();

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        instance.addControl(
            new L.Control.Fullscreen({
                title: {
                    false: 'View fullscreen',
                    true: 'Exit fullscreen',
                },
                position: 'topright',
            })
        );
    };

    return (
        <MapContainer
            center={mapCoords && { lat: mapCoords.lat, lng: mapCoords.lng }}
            className={className}
            bounds={bounds}
            boundsOptions={{ padding: [25, 25] }}
            zoom={mapCoords ? mapCoords.zoom : 12}
            zoomControl={false}
            scrollWheelZoom={false}
            style={{ height: '100%', width: '100%' }}
            maxZoom={maxZoom || 19}
            whenCreated={handleWhenCreated}
        >
            <ZoomControl position="topright" />
            <LayersControl position="topright">
                {baseLayers.map(({ name, id, checked, source }) => (
                    <LayersControl.BaseLayer key={id} checked={checked} name={name}>
                        {source === 'osm' && id === 'osm' &&
                        <TileLayer
                            attribution="© <a href='https://osm.org/copyright' target='_blank'>OpenStreetMap</a> contributors - CC BY-SA"
                            url={`https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png`}
                            maxNativeZoom={18}
                            maxZoom={maxZoom || 19}
                        />}
                        {source === 'osm' && id === 'osmpositron' &&
                        <TileLayer
                            attribution="© <a href='https://osm.org/copyright' target='_blank'>OpenStreetMap</a> contributors - CC BY-SA"
                            url={`https://cartodb-basemaps-a.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png`}
                            maxNativeZoom={18}
                            maxZoom={maxZoom || 19}

                        />}
                        {source === 'osm' && id === 'osmcyclo' &&
                        <TileLayer
                            attribution="© <a href='https://www.cyclosm.org/copyright' target='_blank'>CyclOSM</a> contributors - CC BY-SA"
                            url={`https://{s}.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png`}
                            maxNativeZoom={18}
                            maxZoom={maxZoom || 19}
                        />}
                        {source === 'osm' && id === 'osmtopo' &&
                        <TileLayer
                            attribution="© <a href='https://osm.org/copyright' target='_blank'>OpenStreetMap</a> contributors | © <a href='https://opentopomap.org' target='_blank'>OpenTopoMap</a> - CC BY-SA"
                            url={`https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png`}
                            maxNativeZoom={18}
                            maxZoom={maxZoom || 19}
                        />}
                        {source === 'osm' && id === 'osmhotosm' &&
                        <TileLayer
                            attribution="© <a href='https://www.hotosm.org/updates/2013-09-29_a_new_window_on_openstreetmap_data' target='_blank'>Yohan Boniface & Humanitarian OpenStreetMap Team</a> & <a href='https://osm.org/copyright' target='_blank'>OpenStreetMap</a> - CC BY-SA"
                            url={`https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png`}
                            maxNativeZoom={18}
                            maxZoom={maxZoom || 19}
                        />}
                        {source === 'osm' && id === 'osmoutdoor' &&
                        <TileLayer
                            attribution="© Thunderforest - CC BY-SA"
                            url={`https://{s}.tile.thunderforest.com/outdoors/{z}/{x}/{y}.png`}
                            maxNativeZoom={18}
                            maxZoom={maxZoom || 19}
                        />}
                        

                        {source === 'cache' && 
                        <TileLayer
                            attribution="© <a href='https://maptiler.com/' target='_blank'>MapTiler</a> - © <a href='https://osm.org/copyright'>OpenStreetMap</a> contributors - CC BY-SA"
                            url={`https://maps.utmb.world/maps/${id}/{z}/{x}/{y}.png`}
                        />}
                        {source === 'cartocdn' && 
                        <TileLayer
                            attribution="© <a href='https://carto.com/' target='_blank'>CartoDB</a> - © <a href='https://osm.org/copyright'>OpenStreetMap</a> contributors - CC BY-SA"
                            url={`https://{s}.basemaps.cartocdn.com/${id}/{z}/{x}/{y}.png`}
                        />}
                        {source === 'maptiler' && 
                        <VectorTileLayer
                            attribution="© <a href='https://maptiler.com/' target='_blank'>MapTiler</a> - © <a href='http://osm.org/copyright'>OpenStreetMap</a> contributors - CC BY-SA"
                            styleUrl={`https://api.maptiler.com/maps/${id}/style.json?key=${process.env.NEXT_PUBLIC_MAPTILER_TOKEN}`}
                        />}
                        {source === 'mapbox' && 
                        <TileLayer
                            attribution="MapBox"
                            url={`https://api.mapbox.com/styles/v1/mapbox/${id}/tiles/256/{z}/{x}/{y}@2x?access_token=${process.env.NEXT_PUBLIC_MAPBOX_TOKEN}`}
                        />}
                    </LayersControl.BaseLayer>
                ))}
            </LayersControl>
            {children}
        </MapContainer>
    );
};

export default Map;
